import React, { useId } from "react";

import { Table as BaseTable } from "../atoms/table.tsx";
import { CellTextContentData } from "../cell-variants/types.ts";

import { ContentRow } from "./content-row.tsx";
import { Header, HeaderProps, tableHeadCellIdPrefix } from "./header.tsx";
import { SectionHeader } from "./section-header.tsx";
import { getColumnCount } from "./util.ts";

export type TableProps = {
    sections: TableSectionsData;
    header: Omit<HeaderProps, "tableId">;
};

export type TableSectionsData = Array<{
    title: string;
    rows: CellTextContentData[][];
}>;

export const Table = (props: TableProps) => {
    const { sections, header } = props;
    const tableId = useId().replace(/:/g, "_");

    const columnCount = getColumnCount(sections);

    // Build column header ids aligned with titles (index 0 is the empty row-header column)
    const columnIds = header.titles.map((title, index) => {
        const isEmpty = !title || title.trim() === "";
        return isEmpty ? "" : `${tableHeadCellIdPrefix}${tableId}_${index}`;
    });

    return (
        <BaseTable>
            <Header {...header} tableId={tableId} />
            <tbody>
                {sections.map((section, sectionIndex) => {
                    const groupId = `tblGroup_${tableId}_${sectionIndex}`;
                    return (
                        <React.Fragment key={sectionIndex}>
                            <SectionHeader
                                sectionTitle={section.title}
                                totalColumnCount={columnCount}
                                groupId={groupId}
                            />
                            {section.rows.map((row, rowIndex) => (
                                <ContentRow
                                    key={`${sectionIndex}-${rowIndex}`}
                                    row={row}
                                    rowKey={`${sectionIndex}-${rowIndex}`}
                                    columnTitles={header.titles}
                                    columnIds={columnIds}
                                    groupId={groupId}
                                />
                            ))}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </BaseTable>
    );
};
